import { useCallback, useEffect, useReducer, useRef } from "react";
import { classifyHealth, initialSupervision, nextDelayMs, supervisionReducer } from "@/lib/infraSupervision";

// Boucle de supervision : une seule vérification à la fois (setTimeout chaîné, jamais
// setInterval) ; le rafraîchissement manuel annule la vérification en cours et relance.
export function useInfraSupervision(tick, intervalMs) {
  const [state, dispatch] = useReducer(supervisionReducer, initialSupervision);
  const stateRef = useRef(state);
  const ctrlRef = useRef(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  stateRef.current = state;

  const schedule = useCallback((delay) => {
    clearTimeout(timerRef.current);
    if (delay > 0 && mountedRef.current) timerRef.current = setTimeout(() => runRef.current(false), delay);
  }, []);

  const run = useCallback(async (manual) => {
    if (ctrlRef.current && !manual) return;
    if (ctrlRef.current) ctrlRef.current.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    clearTimeout(timerRef.current);
    const health = await tick(ctrl.signal);
    if (ctrl.signal.aborted || !mountedRef.current) return;
    ctrlRef.current = null;
    const c = classifyHealth(health);
    const at = new Date().toISOString();
    const ev = c.ok ? { type: "success", at } : { type: "failure", at, failureClass: c.failureClass, error: c.error };
    const next = supervisionReducer(stateRef.current, ev);
    dispatch(ev);
    schedule(nextDelayMs(next, intervalMs));
  }, [tick, intervalMs, schedule]);

  const runRef = useRef(run);
  runRef.current = run;

  useEffect(() => {
    mountedRef.current = true;
    run(true);
    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
      if (ctrlRef.current) ctrlRef.current.abort();
      ctrlRef.current = null;
    };
  }, [run]);

  const refresh = useCallback(() => runRef.current(true), []);
  return { state, refresh };
}
